import React from "react";
import { Text, Pill, PopOverMenu } from "components/commons";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import locale from "localization";
import moment from "moment";
import styles from "./price-build-up.module.scss";

const PriceBuildUpStatus = {
  SCHEDULED: "SCHEDULED",
  ACTIVE: "ACTIVE",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED",
};

const formatDate = (dateTime) => {
  if (!dateTime) return null;
  return moment(dateTime).format("DD MMMM YYYY hh:mm A");
};

const StatusPill = ({ status }) => {
  const map = {
    [PriceBuildUpStatus.SCHEDULED]: <Pill cheddar>{locale.scheduled}</Pill>,
    [PriceBuildUpStatus.ACTIVE]: <Pill grass>{locale.active}</Pill>,
    [PriceBuildUpStatus.EXPIRED]: <Pill cement>{locale.expired}</Pill>,
    [PriceBuildUpStatus.CANCELLED]: <Pill deepRed>{locale.cancelled}</Pill>,
  };
  return map[status] || <Text>{status}</Text>;
};

export const mapDataToList = ({
  priceBuildUp = {},
  canEdit = false,
  canCancel = false,
  onView,
  onEdit,
  onCancel,
}) => {
  const {
    // Real API fields
    priceBuildupId,
    priceBuildupCode,
    effectiveAt,

    createdByEmail,
    status: rawStatus,
    type: rawType,
    // Legacy / mock fields (fallbacks)
    station,
    // area,
    createdAt,
  } = priceBuildUp;

  const TYPE_LABELS = {
    "build-up": locale.buildUp,
    "nationwide-movement": locale.nationwideMovement,
    "franchise-movement": locale.franchiseMovement,
  };

  // Normalise: prefer real API fields, fall back to mock/legacy values
  const resolvedId = priceBuildupCode || priceBuildupId;
  // API returns lowercase status ("active") — uppercase for StatusPill
  const status = rawStatus ? rawStatus.toUpperCase() : "UNKNOWN";
  const resolvedEffective = effectiveAt;
  const resolvedProcessedBy = createdByEmail;

  const isScheduled = status === PriceBuildUpStatus.SCHEDULED;
  const isActive = status === PriceBuildUpStatus.ACTIVE;
  const isExpired = status === PriceBuildUpStatus.EXPIRED;

  const options = [
    {
      removable: true,
      content: locale.viewDetails,
      onClick: () => onView(priceBuildUp),
    },
    ...(isScheduled
      ? [
          ...(canEdit
            ? [
                {
                  removable: true,
                  content: locale.editDetails2,
                  onClick: () => onEdit(priceBuildUp),
                },
              ]
            : []),
          ...(canCancel
            ? [
                {
                  removable: true,
                  content: locale.cancel,
                  onClick: () => onCancel(priceBuildUp),
                },
              ]
            : []),
        ]
      : []),
    ...(isActive || isExpired
      ? [
          ...((isActive || isExpired) && canEdit
            ? [
                {
                  removable: true,
                  content: locale.editDetails2,
                  onClick: () => onEdit(priceBuildUp),
                },
              ]
            : []),
          ...(canCancel
            ? [
                {
                  removable: true,
                  content: locale.cancel,
                  onClick: () => onCancel(priceBuildUp),
                },
              ]
            : []),
        ]
      : []),
  ];

  return {
    priceBuildUpId: <Text>{resolvedId}</Text>,
    stationCode: <Text>{station.stationCode || locale.NA}</Text>,
    customerName: <Text>{station.businessName || locale.NA}</Text>,
    stationName: <Text>{station.name || locale.NA}</Text>,
    stationType: <Text>{station.stationType || locale.NA}</Text>,
    island: <Text>{station.island?.toUpperCase() || locale.NA}</Text>,
    area: <Text>{station.area || locale.NA}</Text>,
    effectiveDateTime: <Text>{formatDate(resolvedEffective) || locale.NA}</Text>,
    status: <StatusPill status={status} />,
    processedBy: (
      <>
        <Text>{resolvedProcessedBy || locale.NA}</Text>
        {createdAt && <Text className={styles.subText}>{formatDate(createdAt)}</Text>}
      </>
    ),
    type: <Text>{TYPE_LABELS[rawType] || locale.NA}</Text>,
    actions: (
      <div data-testid={`pbu-list-actions-${resolvedId}`}>
        <PopOverMenu options={options}>
          <MoreVertIcon className={styles.icon} />
        </PopOverMenu>
      </div>
    ),
  };
};

export const mapFilterToRequest = (filterState) => {
  let {
    page = 1,
    perPage = 10,
    searchKey = "",
    searchType = "stationCode",
    stationName,
    status,
    effectiveAt,
  } = filterState;

  if (stationName?.value?.length > 0 && !stationName?.isAll) {
    stationName = stationName.value.map((s) => s.value).join(",");
  } else {
    stationName = undefined;
  }

  if (status?.value?.length > 0 && !status?.isAll) {
    status = status.value.join(",");
  } else {
    status = undefined;
  }

  return {
    page,
    perPage,
    searchKey,
    searchType,
    statuses: status,
    stationName: stationName,
    effectiveAt,
  };
};
